/**
 * 会话状态
 *
 * 用 Vue 的 reactive 而不是 Pinia：管理后台的状态只有「当前登录的管理员」这一份，
 * 引入状态管理库要多一个依赖和一个概念，收益不成正比。
 *
 * 令牌存 localStorage：管理后台是内部工具，没有对外暴露的 XSS 面，
 * 且需要「关掉标签页再打开仍是登录态」。
 * （如果将来这个后台面向更广的运维人群，应改为 httpOnly Cookie。）
 */
import { computed, reactive } from 'vue'

const TOKEN_KEY = 'beadyard_admin_token'
const ADMIN_KEY = 'beadyard_admin_info'

/** 品牌改名前的旧 key（豆格 → 颗绘 / BeadGrid → Beadyard），迁移用 */
const LEGACY_TOKEN_KEY = 'douge_admin_token'
const LEGACY_ADMIN_KEY = 'douge_admin_info'

/**
 * 一次性把旧 key 的会话搬到新 key。
 *
 * 不搬的话，正在登录状态的管理员刷新页面后会被踢回登录页 —— 不至于出事，
 * 但没人知道为什么「突然要我重新登录」。搬完删掉旧 key。
 * 新 key 已有值时以新 key 为准（那是更新的会话）。
 */
function migrateLegacyKeys(): void {
  try {
    const token = localStorage.getItem(TOKEN_KEY)
    const info = localStorage.getItem(ADMIN_KEY)
    if (token === null) {
      const old = localStorage.getItem(LEGACY_TOKEN_KEY)
      if (old !== null) localStorage.setItem(TOKEN_KEY, old)
    }
    if (info === null) {
      const old = localStorage.getItem(LEGACY_ADMIN_KEY)
      if (old !== null) localStorage.setItem(ADMIN_KEY, old)
    }
    localStorage.removeItem(LEGACY_TOKEN_KEY)
    localStorage.removeItem(LEGACY_ADMIN_KEY)
  } catch {
    // 迁移失败不能让页面白屏：最坏结果就是要求重新登录一次
  }
}

function readStored<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    // 存储内容被人为改坏（任务 7.9 的验证场景之一）：当作没有，不要抛错
    return null
  }
}

export interface StoredAdmin {
  id: string
  email: string
  displayName: string
  role: string
}

interface SessionState {
  token: string | null
  admin: StoredAdmin | null
}

export const state = reactive<SessionState>({
  token: null,
  admin: null
})

/** 启动时从 localStorage 恢复，实现「关掉页面再打开仍登录」 */
export function restoreSession(): void {
  // 先做改名迁移，否则老会话会被当成「没有登录」直接踢到登录页
  migrateLegacyKeys()

  const token = localStorage.getItem(TOKEN_KEY)
  const admin = readStored<StoredAdmin>(ADMIN_KEY)

  // 令牌与管理员信息必须同时存在，否则视为无效会话 ——
  // 只有一半的情况（比如手工删了其中一个）会让后续请求必然 401，
  // 不如在这里就当作未登录
  if (token && admin && typeof token === 'string' && token.length > 0) {
    state.token = token
    state.admin = admin
  } else {
    clearSession()
  }
}

export function saveSession(token: string, admin: StoredAdmin): void {
  state.token = token
  state.admin = admin
  localStorage.setItem(TOKEN_KEY, token)
  localStorage.setItem(ADMIN_KEY, JSON.stringify(admin))
}

export function clearSession(): void {
  state.token = null
  state.admin = null
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(ADMIN_KEY)
}

export function getToken(): string | null {
  return state.token
}

export const isLoggedIn = computed(() => state.token !== null && state.admin !== null)

/** 角色判断。前端只用它决定「按钮显不显示」，真正的鉴权在服务端 */
export const isSuperAdmin = computed(() => state.admin?.role === 'super_admin')
